import React, { useState, useEffect, useCallback } from 'react';
import { getUsuarioActual } from '../apiAuth';
import useResponsive from '../utils/useResponsive';
import SeguimientoBancoCard, { copiaEditable } from './SeguimientoBancoCard';

const API = '/api';

const dateDisplay = (v) => {
  if (!v) return '—';
  const [y, m, d] = String(v).slice(0, 10).split('-');
  return `${d}-${m}-${y}`;
};

const SeguimientoClienteDetalle = ({ prospectoId, onVolver }) => {
  const { isMobile } = useResponsive();
  const [cliente, setCliente] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [error, setError] = useState('');
  // Copia editable de cada banco (por id). Se graba/cancela por recuadro completo.
  const [edits, setEdits] = useState({});
  const [msgEvaluacion, setMsgEvaluacion] = useState('');
  const [msgIntegracion, setMsgIntegracion] = useState('');
  const [msgFormalizar, setMsgFormalizar] = useState('');
  const [msgObservaciones, setMsgObservaciones] = useState('');
  const [msgNotaria, setMsgNotaria] = useState('');

  const st = {
    container: { padding: isMobile ? 12 : 24, maxWidth: 1400, margin: '0 auto' },
    stickyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16, position: 'sticky', top: 'var(--app-header-h, 120px)', backgroundColor: '#fff', zIndex: 20, padding: '10px 4px', borderBottom: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' },
    stickyHeaderInfo: { display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' },
    bancosInline: { fontSize: 14, color: '#9ca3af', fontWeight: '400' },
    volverBtn: { padding: '10px 20px', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 14, backgroundColor: '#d1d5db', color: '#374151' },
    card: { backgroundColor: '#F0F0F0', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 20 },
    title: { fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', marginBottom: 16, borderBottom: '2px solid #1a3a6e', paddingBottom: 8 },
    grid4: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 16, marginBottom: 12 },
    field: { marginBottom: 4 },
    label: { display: 'block', fontSize: 12, fontWeight: '600', color: '#6b7280', marginBottom: 4 },
    value: { fontSize: 14, color: '#111827' },
    input: { width: '100%', height: 38, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
    textarea: { width: '100%', minHeight: 38, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' },
    row2: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '320px 1fr', gap: 16, alignItems: 'start' },
    btnGuardar: { padding: '8px 16px', backgroundColor: '#1a3a6e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: '600', marginTop: 8 },
    accionesBar: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, paddingTop: 16, borderTop: '1px solid #d1d5db', flexWrap: 'wrap' },
    btnActualizar: { padding: '9px 20px', backgroundColor: '#1a3a6e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13.5, fontWeight: '600' },
    btnCancelar: { padding: '9px 20px', backgroundColor: '#d1d5db', color: '#374151', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13.5, fontWeight: '600' },
    msgOk: { color: '#059669', fontSize: 13.5, fontWeight: '600' },
    error: { color: '#dc2626', fontSize: 13, marginTop: 8 },
  };

  const [observacionesLocal, setObservacionesLocal] = useState('');
  useEffect(() => { if (cliente) setObservacionesLocal(cliente.observaciones || ''); }, [cliente?.observaciones]); // eslint-disable-line react-hooks/exhaustive-deps

  const [notariaLocal, setNotariaLocal] = useState('');
  useEffect(() => { if (cliente) setNotariaLocal(cliente.notaria_asignada || ''); }, [cliente?.notaria_asignada]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reinicia las copias editables a partir de los datos del servidor.
  const resetEdits = (data) => {
    const map = {};
    (data.bancos || []).forEach(b => { map[b.id] = copiaEditable(b); });
    setEdits(map);
  };

  const cargar = useCallback(() => {
    fetch(`${API}/seguimiento-clientes/${prospectoId}`)
      .then(r => r.json())
      .then(data => { setCliente(data); resetEdits(data); })
      .catch(() => {});
  }, [prospectoId]);

  useEffect(() => {
    fetch(`${API}/catalogos/cat_status_credito`).then(r => r.json()).then(setStatusOptions).catch(() => {});
    cargar();
  }, [cargar]);

  const updEdit = (bancoId, patch) => {
    setEdits(prev => ({ ...prev, [bancoId]: { ...prev[bancoId], ...patch } }));
  };

  const fusionarBanco = (bancoId, actualizado) => {
    const statusNombre = statusOptions.find(s => s.id === actualizado.status_credito_id)?.nombre;
    setCliente(prev => ({
      ...prev,
      bancos: prev.bancos.map(b => b.id === bancoId ? { ...b, ...actualizado, status_nombre: statusNombre } : b),
    }));
  };

  // Graba en secuencia la lista de bancos indicada. Devuelve true si todo salió bien.
  const grabarBancos = async (lista) => {
    for (const b of lista) {
      const datos = edits[b.id] || copiaEditable(b);
      const r = await fetch(`${API}/seguimiento-bancos/${b.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(datos),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.error || `Error al actualizar ${b.banco}`);
        return false;
      }
      const actualizado = await r.json();
      fusionarBanco(b.id, actualizado);
    }
    return true;
  };

  const actualizarEvaluacion = async () => {
    setError(''); setMsgEvaluacion('');
    const ok = await grabarBancos(bancosEvaluacion);
    if (ok) { setMsgEvaluacion('✓ Actualizado'); setTimeout(() => setMsgEvaluacion(''), 3500); }
  };

  const cancelarEvaluacion = () => {
    setError(''); setMsgEvaluacion('');
    setEdits(prev => {
      const next = { ...prev };
      bancosEvaluacion.forEach(b => { next[b.id] = copiaEditable(b); });
      return next;
    });
  };

  const actualizarIntegracion = async () => {
    setError(''); setMsgIntegracion('');
    const ok = await grabarBancos(bancosIntegracion);
    if (ok) { setMsgIntegracion('✓ Actualizado'); setTimeout(() => setMsgIntegracion(''), 3500); }
  };

  const cancelarIntegracion = () => {
    setError(''); setMsgIntegracion('');
    setEdits(prev => {
      const next = { ...prev };
      bancosIntegracion.forEach(b => { next[b.id] = copiaEditable(b); });
      return next;
    });
  };

  const actualizarFormalizar = async () => {
    setError(''); setMsgFormalizar('');
    const ok = await grabarBancos([bancoFormalizar]);
    if (ok) { setMsgFormalizar('✓ Actualizado'); setTimeout(() => setMsgFormalizar(''), 3500); }
  };

  const cancelarFormalizar = () => {
    setError(''); setMsgFormalizar('');
    setEdits(prev => ({ ...prev, [bancoFormalizar.id]: copiaEditable(bancoFormalizar) }));
  };

  const guardarObservaciones = async () => {
    setError(''); setMsgObservaciones('');
    const r = await fetch(`${API}/seguimiento-clientes/${prospectoId}/observaciones`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ observaciones: observacionesLocal }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setError(d.error || 'Error al guardar observaciones');
      return;
    }
    setCliente(prev => ({ ...prev, observaciones: observacionesLocal }));
    setMsgObservaciones('✓ Actualizado');
    setTimeout(() => setMsgObservaciones(''), 3500);
  };

  const guardarNotaria = async () => {
    setError(''); setMsgNotaria('');
    const r = await fetch(`${API}/seguimiento-clientes/${prospectoId}/notaria`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notaria_asignada: notariaLocal }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setError(d.error || 'Error al guardar la notaría asignada');
      return;
    }
    setCliente(prev => ({ ...prev, notaria_asignada: notariaLocal }));
    setMsgNotaria('✓ Actualizado');
    setTimeout(() => setMsgNotaria(''), 3500);
  };

  const setBancoASeguir = async (seguimientoBancoId) => {
    setError('');
    const r = await fetch(`${API}/seguimiento-clientes/${prospectoId}/banco-a-seguir`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seguimiento_banco_id: seguimientoBancoId || null }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setError(d.error || 'Error al asignar banco a seguir');
      return;
    }
    cargar();
  };

  if (!cliente) return <div style={st.container}>Cargando...</div>;

  const bancoFormalizar = cliente.bancos.find(b => b.id === cliente.banco_a_seguir_id) || null;
  const bancosExpediente = cliente.bancos.filter(b => b.id !== cliente.banco_a_seguir_id);
  const bancosIntegracion = bancosExpediente.filter(b => ['En Integración', 'Por firmar formatos'].includes(b.status_nombre));
  // Todo lo que no está en Integración cae aquí (En Evaluación, Autorizado, En Avaluo,
  // En Infonavit, En Notaria, Por Firmar, Firmado, Por Cobrar, Cobrado, Rechazado,
  // Desistimiento) para que un banco nunca desaparezca de la pantalla sin importar
  // qué tan avanzado esté su status, aunque ya no sea el "banco a seguir".
  const bancosEvaluacion = bancosExpediente.filter(b => !bancosIntegracion.includes(b));
  // Opciones del selector: bancos con status Autorizado + el ya elegido (aunque su
  // status ya haya avanzado a 'En Avaluo' u otro, para que siga apareciendo seleccionado).
  const opcionesBancoASeguir = cliente.bancos.filter(b => b.status_nombre === 'Autorizado' || b.id === cliente.banco_a_seguir_id);

  // Administrador Plus consultando el cliente de un usuario asignado: solo lectura
  // (el backend también rechaza cambios de status, notaría, observaciones, etc.).
  const usuarioActual = getUsuarioActual();
  const soloLectura = usuarioActual?.rol === 'administrador_plus' && cliente.usuario_id !== usuarioActual.id;

  return (
    <div style={st.container}>
      <div style={st.stickyHeader}>
        <div style={st.stickyHeaderInfo}>
          <h2 style={{ fontSize: 22, fontWeight: 'bold', margin: 0 }}>{(cliente.nombre_cliente || '').toUpperCase()}</h2>
          {cliente.bancos.length > 0 && (
            <span style={st.bancosInline}>{cliente.bancos.map(b => b.banco).join(' · ')}</span>
          )}
        </div>
        <button style={st.volverBtn} onClick={onVolver}>← Volver</button>
      </div>

      {soloLectura && (
        <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14 }}>
          👁 <strong>Solo lectura</strong> — este cliente pertenece a otro usuario; puede consultarlo pero no modificarlo.
        </div>
      )}
      <fieldset disabled={soloLectura} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      <div style={st.card}>
        <h3 style={st.title}>Información del Cliente</h3>
        <div style={st.grid4}>
          <div style={st.field}><label style={st.label}>ID Cte</label><div style={st.value}>{cliente.prospecto_id}</div></div>
          <div style={st.field}><label style={st.label}>Celular</label><div style={st.value}>{cliente.telefono_celular || '—'}</div></div>
          <div style={st.field}><label style={st.label}>Fecha Inicio Contacto</label><div style={st.value}>{dateDisplay(cliente.fecha_inicio_contacto)}</div></div>
          <div style={st.field}><label style={st.label}>RFC</label><div style={st.value}>{cliente.rfc || '—'}</div></div>
          <div style={st.field}><label style={st.label}>Estado Civil</label><div style={st.value}>{cliente.estado_civil || '—'}</div></div>
          <div style={st.field}><label style={st.label}>Régimen Casado</label><div style={st.value}>{cliente.regimen_matrimonio || '—'}</div></div>
        </div>
        <div style={st.row2}>
          <div style={st.field}>
            <label style={st.label}>Banco a Seguir con Formalización</label>
            <select value={cliente.banco_a_seguir_id || ''} onChange={e => setBancoASeguir(e.target.value)} style={st.input}>
              <option value="">-- Elegir banco autorizado --</option>
              {opcionesBancoASeguir.map(b => <option key={b.id} value={b.id}>{b.banco}</option>)}
            </select>
            {cliente.banco_a_seguir_id && (
              <div style={{ marginTop: 12 }}>
                <label style={st.label}>Notaría Asignada</label>
                <input type="text" value={notariaLocal} onChange={e => setNotariaLocal(e.target.value)} style={st.input} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                  <button style={st.btnGuardar} onClick={guardarNotaria}>Actualizar Notaría</button>
                  {msgNotaria && <span style={st.msgOk}>{msgNotaria}</span>}
                </div>
              </div>
            )}
          </div>
          <div style={st.field}>
            <label style={st.label}>Observaciones</label>
            <textarea value={observacionesLocal} onChange={e => setObservacionesLocal(e.target.value)} style={st.textarea} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={st.btnGuardar} onClick={guardarObservaciones}>Actualizar Observaciones</button>
              {msgObservaciones && <span style={{ ...st.msgOk, marginTop: 8 }}>{msgObservaciones}</span>}
            </div>
          </div>
        </div>
        {error && <div style={st.error}>{error}</div>}
      </div>

      {bancoFormalizar && (
        <div style={st.card}>
          <h3 style={st.title}>Banco a Formalizar</h3>
          <SeguimientoBancoCard
            key={bancoFormalizar.id}
            banco={bancoFormalizar}
            local={edits[bancoFormalizar.id]}
            onChange={(patch) => updEdit(bancoFormalizar.id, patch)}
            statusOptions={statusOptions}
            mostrarChecklist={true}
            onDocumentoActualizado={fusionarBanco}
          />
          <div style={st.accionesBar}>
            <button style={st.btnActualizar} onClick={actualizarFormalizar}>Actualizar</button>
            <button style={st.btnCancelar} onClick={cancelarFormalizar}>Cancelar</button>
            {msgFormalizar && <span style={st.msgOk}>{msgFormalizar}</span>}
          </div>
        </div>
      )}

      {bancosExpediente.length === 0 && (
        <div style={st.card}>
          <h3 style={st.title}>Expedientes en Integración e Ingresados a Banco</h3>
          <p style={{ color: '#6b7280', fontSize: 14 }}>Sin bancos capturados todavía. En cuanto se llene el formulario de un banco para este prospecto, aparecerá aquí automáticamente.</p>
        </div>
      )}

      {bancosEvaluacion.length > 0 && (
        <div style={st.card}>
          <h3 style={st.title}>Expedientes en Evaluación</h3>
          {bancosEvaluacion.map(b => (
            <SeguimientoBancoCard
              key={b.id}
              banco={b}
              local={edits[b.id]}
              onChange={(patch) => updEdit(b.id, patch)}
              statusOptions={statusOptions}
              mostrarChecklist={false}
              soloHastaAutorizado={true}
              onDocumentoActualizado={fusionarBanco}
            />
          ))}
          <div style={st.accionesBar}>
            <button style={st.btnActualizar} onClick={actualizarEvaluacion}>Actualizar</button>
            <button style={st.btnCancelar} onClick={cancelarEvaluacion}>Cancelar</button>
            {msgEvaluacion && <span style={st.msgOk}>{msgEvaluacion}</span>}
          </div>
        </div>
      )}

      {bancosIntegracion.length > 0 && (
        <div style={st.card}>
          <h3 style={st.title}>Expedientes en Integración</h3>
          {bancosIntegracion.map(b => (
            <SeguimientoBancoCard
              key={b.id}
              banco={b}
              local={edits[b.id]}
              onChange={(patch) => updEdit(b.id, patch)}
              statusOptions={statusOptions}
              mostrarChecklist={false}
              soloHastaAutorizado={true}
              onDocumentoActualizado={fusionarBanco}
            />
          ))}
          <div style={st.accionesBar}>
            <button style={st.btnActualizar} onClick={actualizarIntegracion}>Actualizar</button>
            <button style={st.btnCancelar} onClick={cancelarIntegracion}>Cancelar</button>
            {msgIntegracion && <span style={st.msgOk}>{msgIntegracion}</span>}
          </div>
        </div>
      )}
      </fieldset>
    </div>
  );
};

export default SeguimientoClienteDetalle;
